/**
 * Password validation utility functions
 * Reusable across registration, password change, and other password-related forms
 */

/**
 * Calculate password strength and requirements
 * @param {string} password - The password to evaluate
 * @returns {Object} - Object containing strength score, label, and requirements met
 */
export const calculatePasswordStrength = (password) => {
  if (!password) {
    return { 
      strength: 0, 
      label: '', 
      requirements: {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
        noSpaces: false
      }
    };
  }

  const requirements = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
    noSpaces: !/\s/.test(password)
  };

  const metCount = Object.values(requirements).filter(Boolean).length;
  const strength = Math.min(metCount, 6);
  
  let label = '';
  if (strength <= 2) label = 'Weak';
  else if (strength <= 4) label = 'Medium';
  else if (strength <= 5) label = 'Strong';
  else label = 'Very Strong';

  return { strength, label, requirements };
};

/**
 * Validate password against strong password requirements
 * @param {string} password - The password to validate
 * @param {string} currentPassword - Optional: current password to check if new password is different
 * @returns {Object} - Object with isValid boolean and error message
 */
export const validateStrongPassword = (password, currentPassword = null) => {
  if (!password || !password.trim()) {
    return {
      isValid: false,
      error: 'Password is required'
    };
  }

  if (password.length < 8) {
    return {
      isValid: false,
      error: 'Password must be at least 8 characters'
    };
  }

  if (!/[A-Z]/.test(password)) {
    return {
      isValid: false,
      error: 'Password must contain at least one uppercase letter (A-Z)'
    };
  }

  if (!/[a-z]/.test(password)) {
    return {
      isValid: false,
      error: 'Password must contain at least one lowercase letter (a-z)'
    };
  }

  if (!/[0-9]/.test(password)) {
    return {
      isValid: false,
      error: 'Password must contain at least one number (0-9)'
    };
  }

  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
    return {
      isValid: false,
      error: 'Password must contain at least one special character (!@#$%^&*...)'
    };
  }

  if (/\s/.test(password)) {
    return {
      isValid: false,
      error: 'Password cannot contain spaces'
    };
  }

  if (currentPassword && password === currentPassword) {
    return {
      isValid: false,
      error: 'New password must be different from current password'
    };
  }

  return {
    isValid: true,
    error: null
  };
};

/**
 * Validate password form (for password change forms)
 * @param {Object} passwordData - Object containing currentPassword, newPassword, confirmPassword
 * @returns {Object} - Object with isValid boolean and errors object
 */
export const validatePasswordForm = (passwordData) => {
  const errors = {};
  
  // Validate current password
  if (!passwordData.currentPassword || !passwordData.currentPassword.trim()) {
    errors.currentPassword = 'Current password is required';
  }
  
  // Validate new password
  if (!passwordData.newPassword || !passwordData.newPassword.trim()) {
    errors.newPassword = 'New password is required';
  } else {
    const passwordValidation = validateStrongPassword(
      passwordData.newPassword,
      passwordData.currentPassword
    );
    if (!passwordValidation.isValid) {
      errors.newPassword = passwordValidation.error;
    }
  }
  
  // Validate password confirmation
  if (passwordData.newPassword !== passwordData.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Validate password for registration (no current password check)
 * @param {string} password - The password to validate
 * @param {string} confirmPassword - The password confirmation
 * @returns {Object} - Object with isValid boolean and errors object
 */
export const validateRegistrationPassword = (password, confirmPassword) => {
  const errors = {};
  
  if (!password || !password.trim()) {
    errors.password = 'Password is required';
  } else {
    const passwordValidation = validateStrongPassword(password);
    if (!passwordValidation.isValid) {
      errors.password = passwordValidation.error;
    }
  }
  
  if (password !== confirmPassword) {
    errors.confirmPassword = 'Passwords do not match';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Get password requirements list for display
 * @returns {Array} - Array of requirement objects with label and key
 */
export const getPasswordRequirements = () => [
  { key: 'length', label: 'At least 8 characters' },
  { key: 'uppercase', label: 'One uppercase letter (A-Z)' },
  { key: 'lowercase', label: 'One lowercase letter (a-z)' },
  { key: 'number', label: 'One number (0-9)' },
  { key: 'special', label: 'One special character (!@#$%^&*...)' },
  { key: 'noSpaces', label: 'No spaces' }
];

